import { useDialog } from '@/contexts/DialogContext';
import { Permission, useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { Markdown } from '@/components/Markdown';
import DetailDrawer from '@/components/DetailDrawer/DetailDrawer';
import PermissionDeniedNotice from '@/components/PermissionDeniedNotice/PermissionDeniedNotice';
import PaginationFooter, { getVisiblePaginationPages } from '@/components/Pagination/PaginationFooter';
import { useTranslation } from '@client-app/i18n';
import { featureService } from '@common/api/featureService';
import Skeleton from '@common/components/Skeleton';
import { ArrowRight, CheckCircle, CircleNotch, Lightning, Power, Trash, WarningCircle } from '@phosphor-icons/react';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    deleteWorkspaceManagedSkill,
    getWorkspaceManagedSkillDetail,
    listWorkspaceManagedSkills,
    WorkspaceSkillsPermissionError,
    type WorkspaceManagedSkill,
} from './workspaceSkillsApi';
import { readStoredWorkspaceSkillsSettings, writeStoredWorkspaceSkillsSettings } from './workspaceSkillsSettingsStorage';

type WorkspaceSkillsFeatureSettings = {
    enabled: boolean;
    activatedAt: string | null;
};

const WORKSPACE_SKILLS_PAGE_SIZE = 10;

const asRecord = (value: unknown): Record<string, unknown> =>
    value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const getWorkspaceSkillsFeatureSettings = (value: unknown): WorkspaceSkillsFeatureSettings => {
    const settings = asRecord(value);
    return {
        enabled: settings.enabled === true,
        activatedAt: typeof settings.activatedAt === 'string' && settings.activatedAt.trim() ? settings.activatedAt : null,
    };
};

const canUseLocalWorkspaceSkillsSettingsFallback = () => import.meta.env.DEV === true;

const httpStatusFromError = (error: unknown): number | null => {
    if (!error || typeof error !== 'object') {
        return null;
    }
    const record = error as {
        response?: {
            status?: unknown;
        };
        status?: unknown;
    };
    const status = Number(record.response?.status ?? record.status);
    return Number.isFinite(status) ? status : null;
};

const shouldUseLocalWorkspaceSkillsSettingsFallback = (error: unknown) =>
    canUseLocalWorkspaceSkillsSettingsFallback() && httpStatusFromError(error) === null;

const formatSkillUpdatedAt = (value: string, locale: string): string => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return new Intl.DateTimeFormat(locale === 'zh-CN' ? 'zh-CN' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    }).format(date);
};

const getSkillDetailContent = (value: string): string => value.replace(/^\s*---\r?\n[\s\S]*?\r?\n---\s*(?:\r?\n|$)/, '').trim();

const WorkspaceSkillCardSkeleton = () => (
    <article
        aria-hidden="true"
        className="flex min-h-[208px] flex-col rounded-xl border border-[#E8EBF3] bg-[#FCFCFC] p-5 shadow-[0_10px_24px_rgba(26,22,47,0.05)]"
    >
        <div className="flex items-start gap-3">
            <Skeleton width="40px" height="40px" className="shrink-0 animate-pulse rounded-[10px]" />
            <div className="min-w-0 flex-1 space-y-2">
                <Skeleton width="42%" height="16px" className="animate-pulse rounded-md" />
                <Skeleton width="92%" height="13px" className="animate-pulse rounded-md" />
                <Skeleton width="68%" height="13px" className="animate-pulse rounded-md" />
            </div>
        </div>
        <div className="mt-auto grid grid-cols-[88px_minmax(0,1fr)] gap-x-2 gap-y-2 border-t border-[#EEF1F6] pt-4">
            <Skeleton width="68px" height="11px" className="animate-pulse rounded-md" />
            <Skeleton width="68%" height="12px" className="animate-pulse rounded-md" />
            <Skeleton width="80px" height="11px" className="animate-pulse rounded-md" />
            <Skeleton width="112px" height="12px" className="animate-pulse rounded-md" />
        </div>
    </article>
);

const SkillDetailSkeleton = ({ label }: { label: string }) => (
    <div role="status" aria-label={label} className="space-y-7 rounded-xl bg-[#F8F9FC] p-5">
        <span className="sr-only">{label}</span>
        <div className="space-y-3">
            <Skeleton width="44%" height="20px" className="animate-pulse rounded-md" />
            <Skeleton width="100%" height="13px" className="animate-pulse rounded-md" />
            <Skeleton width="92%" height="13px" className="animate-pulse rounded-md" />
            <Skeleton width="66%" height="13px" className="animate-pulse rounded-md" />
        </div>
        <div className="space-y-3">
            <Skeleton width="32%" height="18px" className="animate-pulse rounded-md" />
            <Skeleton width="96%" height="13px" className="animate-pulse rounded-md" />
            <Skeleton width="88%" height="13px" className="animate-pulse rounded-md" />
            <Skeleton width="58%" height="13px" className="animate-pulse rounded-md" />
        </div>
        <div className="space-y-3">
            <Skeleton width="38%" height="18px" className="animate-pulse rounded-md" />
            <Skeleton width="100%" height="13px" className="animate-pulse rounded-md" />
            <Skeleton width="72%" height="13px" className="animate-pulse rounded-md" />
        </div>
    </div>
);

const WorkspaceSkillsSwitch = ({
    checked,
    disabled,
    label,
    onChange,
}: {
    checked: boolean;
    disabled?: boolean;
    label: string;
    onChange: (checked: boolean) => void;
}) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={clsx(
            'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full p-0.5 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2F7BFF]/20',
            checked ? 'bg-[#2F7BFF]' : 'bg-[#E2E8F0]',
            disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-[#D8E0EB]',
            checked && !disabled && 'hover:bg-[#256DE5]',
        )}
    >
        <span
            className={clsx(
                'pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-[0_1px_2px_rgba(15,23,42,0.22)] transition-transform duration-200',
                checked ? 'translate-x-4' : 'translate-x-0',
            )}
        />
    </button>
);

const WorkspaceSkillsHeroMockup = ({ t }: { t: ReturnType<typeof useTranslation>['t'] }) => (
    <div
        aria-hidden="true"
        className="pointer-events-none absolute left-[59%] top-[58px] z-0 hidden w-[43%] min-w-[410px] max-w-[560px] rounded-[24px] border border-[#E7EAF4] bg-[#F7F9FC] p-6 shadow-[0_16px_40px_rgba(26,22,47,0.06)] xl:block 2xl:left-[56%] 2xl:w-[46%] 2xl:max-w-[620px]"
    >
        <div className="rounded-2xl border border-[#E8EBF3] bg-white p-5">
            <div className="flex items-center gap-3">
                <span className="flex size-9 shrink-0 items-center justify-center rounded-[10px] border border-[#DDE5F1] bg-[#F8FAFC] text-[#666F8D]">
                    <CheckCircle size={18} weight="bold" />
                </span>
                <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-semibold text-[#8A93B2]">{t('website.workspaceSkills.previewCompletedTask')}</p>
                    <div className="mt-2 h-3 w-3/4 rounded bg-[#E8EDF5]" />
                </div>
                <span className="rounded-full bg-[#ECFDF3] px-2 py-1 text-[10px] font-semibold text-[#027A48]">
                    {t('website.workspaceSkills.previewReviewed')}
                </span>
            </div>
            <div className="mt-5 space-y-2.5">
                <div className="h-2.5 w-full rounded bg-[#EEF1F6]" />
                <div className="h-2.5 w-[88%] rounded bg-[#EEF1F6]" />
                <div className="h-2.5 w-[62%] rounded bg-[#EEF1F6]" />
            </div>
        </div>

        <div className="flex h-12 items-center justify-center text-[#8A93B2]">
            <ArrowRight size={20} weight="bold" className="rotate-90" />
        </div>

        <div className="rounded-2xl border border-[#D9E4F5] bg-white p-5 shadow-[0_10px_28px_rgba(26,22,47,0.06)]">
            <div className="flex items-start gap-3">
                <span className="flex size-10 shrink-0 items-center justify-center rounded-[10px] border border-[#DDE5F1] bg-white text-[#666F8D]">
                    <Lightning size={19} weight="bold" />
                </span>
                <div className="min-w-0 flex-1">
                    <p className="text-[12px] font-semibold text-[#1A162F]">{t('website.workspaceSkills.previewReusableSkill')}</p>
                    <div className="mt-2.5 h-3 w-2/3 rounded bg-[#E8EDF5]" />
                    <div className="mt-2 h-2.5 w-full rounded bg-[#EEF1F6]" />
                    <div className="mt-2 h-2.5 w-4/5 rounded bg-[#EEF1F6]" />
                </div>
            </div>
            <div className="mt-5 flex items-center gap-2 border-t border-[#EEF1F6] pt-4 text-[10px] font-semibold text-[#667085]">
                <span className="size-1.5 rounded-full bg-[#12B76A]" />
                {t('website.workspaceSkills.previewReady')}
            </div>
        </div>
    </div>
);

const WorkspaceSkillsGateCard = ({
    canManage,
    isSaving,
    onEnable,
    t,
}: {
    canManage: boolean;
    isSaving: boolean;
    onEnable: () => void;
    t: ReturnType<typeof useTranslation>['t'];
}) => (
    <section className="relative overflow-hidden rounded-[28px] border border-[#E7EAF4] bg-white px-6 py-8 shadow-[0_22px_70px_rgba(26,22,47,0.08)] sm:px-9 sm:py-10 lg:px-[52px] lg:py-[44px] xl:px-[56px]">
        <WorkspaceSkillsHeroMockup t={t} />
        <div className="relative z-10 flex min-h-[320px] max-w-[720px] flex-col justify-center lg:min-h-[360px] xl:w-[56%]">
            <h1 className="max-w-[620px] text-[28px] font-semibold leading-[1.08] tracking-tight text-[#17133F] sm:text-[30px] xl:text-[32px] 2xl:text-[36px]">
                {t('website.workspaceSkills.guideTitle')}
            </h1>
            <p className="mt-4 max-w-[520px] text-[14px] font-normal leading-[1.58] text-[#666F8D] sm:text-[15px]">
                {t('website.workspaceSkills.guideDescription')}
            </p>
            <div className="mt-6 flex flex-col items-start gap-3">
                <button
                    type="button"
                    onClick={onEnable}
                    disabled={!canManage || isSaving}
                    className={clsx(
                        'inline-flex h-[50px] min-w-[280px] max-w-full items-center justify-center gap-2 whitespace-nowrap rounded-[10px] bg-[#1A162F] px-6 text-[15px] font-semibold text-white shadow-[0_4px_12px_rgba(26,22,47,0.15)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#2B2744] active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/20',
                        (!canManage || isSaving) && 'cursor-not-allowed opacity-60 shadow-none hover:translate-y-0 hover:bg-[#1A162F]',
                    )}
                >
                    {isSaving ? (
                        <CircleNotch size={18} weight="bold" className="shrink-0 animate-spin" />
                    ) : (
                        <Power size={18} weight="bold" className="shrink-0" />
                    )}
                    <span>{t('website.workspaceSkills.turnOn')}</span>
                </button>
                {!canManage ? (
                    <span className="text-[12px] font-medium leading-5 text-[#8A93B2]">{t('website.workspaceSkills.ownerOnlyHint')}</span>
                ) : null}
            </div>
            <div className="mt-6 flex max-w-[560px] items-start gap-3 text-[12px] font-normal leading-5 text-[#666F8D] sm:text-[13px]">
                <CheckCircle size={20} weight="bold" className="mt-px shrink-0" />
                <p>{t('website.workspaceSkills.creationNote')}</p>
            </div>
        </div>
    </section>
);

const WorkspaceSkillsGateSkeleton = () => (
    <div
        role="status"
        aria-busy="true"
        className="min-h-[448px] animate-pulse rounded-[28px] border border-[#E7EAF4] bg-white px-6 py-11 shadow-[0_22px_70px_rgba(26,22,47,0.06)] sm:px-9 lg:px-[52px]"
    >
        <Skeleton width="52%" height="34px" className="rounded-lg" />
        <div className="mt-5 space-y-3">
            <Skeleton width="48%" height="14px" className="rounded-md" />
            <Skeleton width="43%" height="14px" className="rounded-md" />
        </div>
        <Skeleton width="230px" height="50px" className="mt-7 rounded-[10px]" />
        <Skeleton width="46%" height="14px" className="mt-7 rounded-md" />
    </div>
);

const WorkspaceSkillsSettings = () => {
    const { t, locale } = useTranslation();
    const { currentWorkspace, permissions, refreshWorkspaces } = useAuth();
    const { showToast } = useToast();
    const { showDialog } = useDialog();
    const workspaceId = currentWorkspace?.id || '';
    const canManageWorkspaceSkills = permissions.checkPermissions([Permission.WORKSPACE_SKILL_MANAGE]);
    const remoteFeatureSettings = getWorkspaceSkillsFeatureSettings(asRecord(currentWorkspace?.settings).workspaceSkills);
    const storedFeatureSettings = canUseLocalWorkspaceSkillsSettingsFallback() ? readStoredWorkspaceSkillsSettings(workspaceId) : null;
    const featureSettings =
        remoteFeatureSettings.enabled || remoteFeatureSettings.activatedAt
            ? remoteFeatureSettings
            : storedFeatureSettings || remoteFeatureSettings;
    const [isEnabled, setIsEnabled] = useState(featureSettings.enabled);
    const [hasStarted, setHasStarted] = useState(featureSettings.enabled || Boolean(featureSettings.activatedAt));
    const [isSavingSettings, setIsSavingSettings] = useState(false);
    const [settingsError, setSettingsError] = useState<string | null>(null);
    const [skills, setSkills] = useState<WorkspaceManagedSkill[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalSkills, setTotalSkills] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [permissionDenied, setPermissionDenied] = useState(false);
    const [deletingSkillId, setDeletingSkillId] = useState<string | null>(null);
    const [detailSkill, setDetailSkill] = useState<WorkspaceManagedSkill | null>(null);
    const [skillMarkdown, setSkillMarkdown] = useState('');
    const [detailError, setDetailError] = useState<string | null>(null);
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);
    const skillsRequestId = useRef(0);
    const detailCreator = detailSkill
        ? detailSkill.createdByEmail || detailSkill.createdByName || t('website.workspaceSkills.unknownCreator')
        : '';

    useEffect(() => {
        setIsEnabled(featureSettings.enabled);
        setHasStarted(featureSettings.enabled || Boolean(featureSettings.activatedAt));
        setSettingsError(null);
    }, [featureSettings.activatedAt, featureSettings.enabled, workspaceId]);

    useEffect(() => {
        skillsRequestId.current += 1;
        setCurrentPage(1);
        setTotalSkills(0);
    }, [workspaceId]);

    const loadSkills = useCallback(async () => {
        const requestId = skillsRequestId.current + 1;
        skillsRequestId.current = requestId;
        if (!workspaceId || !hasStarted || !canManageWorkspaceSkills) {
            setSkills([]);
            setTotalSkills(0);
            setErrorMessage(null);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setErrorMessage(null);
        setPermissionDenied(false);
        try {
            const response = await listWorkspaceManagedSkills({
                page: currentPage,
                pageSize: WORKSPACE_SKILLS_PAGE_SIZE,
                workspaceId,
            });
            if (skillsRequestId.current !== requestId) {
                return;
            }
            if (currentPage > response.totalPages) {
                setCurrentPage(response.totalPages);
                return;
            }
            setSkills(response.skills);
            setTotalSkills(response.total);
        } catch (error) {
            if (skillsRequestId.current !== requestId) {
                return;
            }
            setSkills([]);
            setTotalSkills(0);
            if (error instanceof WorkspaceSkillsPermissionError) {
                setPermissionDenied(true);
                setErrorMessage(null);
            } else {
                setErrorMessage(error instanceof Error ? error.message : t('website.workspaceSkills.loadFailed'));
            }
        } finally {
            if (skillsRequestId.current === requestId) {
                setIsLoading(false);
            }
        }
    }, [canManageWorkspaceSkills, currentPage, hasStarted, t, workspaceId]);

    useEffect(() => {
        void loadSkills();
    }, [loadSkills]);

    const sortedSkills = useMemo(
        () => [...skills].sort((left, right) => Date.parse(right.updatedAt) - Date.parse(left.updatedAt)),
        [skills],
    );
    const totalPages = Math.max(1, Math.ceil(totalSkills / WORKSPACE_SKILLS_PAGE_SIZE));
    const visiblePages = useMemo(
        () => getVisiblePaginationPages(currentPage, totalPages),
        [currentPage, totalPages],
    );

    const updateWorkspaceSkillsEnabled = useCallback(
        async (nextEnabled: boolean) => {
            if (!workspaceId || !permissions.isOwner || isSavingSettings) {
                return;
            }

            if (!nextEnabled) {
                const confirmed = await showDialog({
                    title: t('website.workspaceSkills.pauseTitle'),
                    subtitle: t('website.workspaceSkills.pauseDescription'),
                    confirmText: t('website.workspaceSkills.pauseConfirm'),
                    cancelText: t('common.cancel'),
                    destructive: true,
                });
                if (!confirmed) {
                    return;
                }
            }

            const previousEnabled = isEnabled;
            const previousHasStarted = hasStarted;
            const currentSettings = asRecord(currentWorkspace?.settings);
            const currentFeatureSettings = asRecord(currentSettings.workspaceSkills);
            const activatedAt = featureSettings.activatedAt || new Date().toISOString();
            const nextFeatureSettings = {
                ...currentFeatureSettings,
                enabled: nextEnabled,
                activatedAt,
            };

            setIsSavingSettings(true);
            setSettingsError(null);
            setIsEnabled(nextEnabled);
            if (nextEnabled) {
                setHasStarted(true);
            }
            try {
                await featureService.updateWorkspace(workspaceId, {
                    name: currentWorkspace?.name,
                    logoUrl: currentWorkspace?.logo_url || undefined,
                    settings: {
                        ...currentSettings,
                        workspaceSkills: nextFeatureSettings,
                    },
                });
                if (canUseLocalWorkspaceSkillsSettingsFallback()) {
                    writeStoredWorkspaceSkillsSettings(workspaceId, {
                        enabled: nextEnabled,
                        activatedAt,
                    });
                }
                await refreshWorkspaces({ silent: true }).catch(() => undefined);
            } catch (error) {
                if (shouldUseLocalWorkspaceSkillsSettingsFallback(error)) {
                    writeStoredWorkspaceSkillsSettings(workspaceId, {
                        enabled: nextEnabled,
                        activatedAt,
                    });
                    setHasStarted(true);
                } else {
                    setIsEnabled(previousEnabled);
                    setHasStarted(previousHasStarted);
                    setSettingsError(t('website.workspaceSkills.settingsUpdateFailed'));
                }
            } finally {
                setIsSavingSettings(false);
            }
        },
        [
            currentWorkspace?.settings,
            currentWorkspace?.logo_url,
            currentWorkspace?.name,
            featureSettings.activatedAt,
            hasStarted,
            isEnabled,
            isSavingSettings,
            permissions.isOwner,
            refreshWorkspaces,
            showDialog,
            showToast,
            t,
            workspaceId,
        ],
    );

    const handleDelete = useCallback(
        async (skill: WorkspaceManagedSkill) => {
            if (!workspaceId || deletingSkillId) {
                return;
            }

            const confirmed = await showDialog({
                title: t('website.workspaceSkills.deleteSkillTitle'),
                subtitle: t('website.workspaceSkills.deleteSkillDescription', {
                    name: skill.displayName,
                }),
                confirmText: t('website.workspaceSkills.deleteSkillConfirm'),
                cancelText: t('common.cancel'),
                destructive: true,
            });
            if (!confirmed) {
                return;
            }

            setDeletingSkillId(skill.id);
            try {
                await deleteWorkspaceManagedSkill({ workspaceId, skillId: skill.id });
                const nextTotal = Math.max(0, totalSkills - 1);
                const nextTotalPages = Math.max(1, Math.ceil(nextTotal / WORKSPACE_SKILLS_PAGE_SIZE));
                const nextPage = Math.min(currentPage, nextTotalPages);
                setTotalSkills(nextTotal);
                if (nextPage === currentPage) {
                    await loadSkills();
                } else {
                    setCurrentPage(nextPage);
                }
                showToast(t('website.workspaceSkills.deleteSkillSuccess'), 'success');
            } catch (error) {
                showToast(error instanceof Error ? error.message : t('website.workspaceSkills.deleteSkillFailed'), 'error');
            } finally {
                setDeletingSkillId(null);
            }
        },
        [currentPage, deletingSkillId, loadSkills, showDialog, showToast, t, totalSkills, workspaceId],
    );

    const closeSkillDetail = useCallback(() => {
        setDetailSkill(null);
        setSkillMarkdown('');
        setDetailError(null);
        setIsLoadingDetail(false);
    }, []);

    const handleViewSkillDetail = useCallback(
        async (skill: WorkspaceManagedSkill) => {
            if (!workspaceId || isLoadingDetail) {
                return;
            }

            setDetailSkill(skill);
            setSkillMarkdown('');
            setDetailError(null);
            setIsLoadingDetail(true);
            try {
                const detail = await getWorkspaceManagedSkillDetail({
                    workspaceId,
                    skillId: skill.id,
                });
                setSkillMarkdown(detail.skillMarkdown);
            } catch (error) {
                setDetailError(error instanceof Error ? error.message : t('website.workspaceSkills.detailLoadFailed'));
            } finally {
                setIsLoadingDetail(false);
            }
        },
        [isLoadingDetail, t, workspaceId],
    );

    if (permissions.isLoading) {
        return <WorkspaceSkillsGateSkeleton />;
    }

    if (!canManageWorkspaceSkills) {
        return (
            <PermissionDeniedNotice
                title={t('website.workspaceSkills.permissionDeniedTitle')}
                description={t('website.workspaceSkills.permissionDeniedDescription')}
            />
        );
    }

    if (permissionDenied) {
        return (
            <PermissionDeniedNotice
                title={t('website.workspaceSkills.permissionDeniedTitle')}
                description={t('website.workspaceSkills.permissionDeniedDescription')}
            />
        );
    }

    if (!hasStarted) {
        return (
            <div className="[overflow-anchor:none]">
                {settingsError ? (
                    <p className="mb-4 rounded-lg border border-[#F7C8C5] bg-[#FFF6F5] px-4 py-3 text-[12px] leading-5 text-[#B42318]">
                        {settingsError}
                    </p>
                ) : null}
                <WorkspaceSkillsGateCard
                    canManage={permissions.isOwner}
                    isSaving={isSavingSettings}
                    onEnable={() => void updateWorkspaceSkillsEnabled(true)}
                    t={t}
                />
            </div>
        );
    }

    return (
        <>
            <div className="pb-4">
                <div className="mb-6">
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                            <h1 className="text-[20px] font-medium tracking-[-0.01em] text-[#1A162F]">
                                {t('website.settingsModal.workspaceSkills')}
                            </h1>
                            {permissions.isOwner ? (
                                isEnabled ? (
                                    <button
                                        type="button"
                                        onClick={() => void updateWorkspaceSkillsEnabled(false)}
                                        disabled={isSavingSettings}
                                        className="flex items-center gap-1.5 text-[13px] font-medium text-[#666F8D] transition-colors hover:text-[#1A162F] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/20 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        {isSavingSettings ? (
                                            <CircleNotch size={14} weight="regular" className="animate-spin text-[#8A93B2]" />
                                        ) : null}
                                        <span>{t('website.workspaceSkills.pauseAction')}</span>
                                    </button>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        {isSavingSettings ? (
                                            <CircleNotch size={14} weight="regular" className="animate-spin text-[#8A93B2]" />
                                        ) : (
                                            <WorkspaceSkillsSwitch
                                                checked={false}
                                                disabled={isSavingSettings}
                                                label={t('website.workspaceSkills.resumeLabel')}
                                                onChange={(checked) => {
                                                    if (checked) {
                                                        void updateWorkspaceSkillsEnabled(true);
                                                    }
                                                }}
                                            />
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => void updateWorkspaceSkillsEnabled(true)}
                                            disabled={isSavingSettings}
                                            className="text-[13px] font-medium text-[#666F8D] transition-colors hover:text-[#1A162F] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/20 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            {t('website.workspaceSkills.resumeAction')}
                                        </button>
                                    </div>
                                )
                            ) : null}
                        </div>
                        <p className="mt-3 max-w-[700px] text-sm leading-6 text-[#666F8D]">
                            {t(isEnabled ? 'website.workspaceSkills.subtitle' : 'website.workspaceSkills.pausedDescription')}
                        </p>
                    </div>
                </div>
                {settingsError ? (
                    <p className="mb-4 rounded-lg border border-[#F7C8C5] bg-[#FFF6F5] px-4 py-3 text-[12px] leading-5 text-[#B42318]">
                        {settingsError}
                    </p>
                ) : null}
                {isLoading ? (
                    <div role="status" aria-label={t('website.workspaceSkills.loading')} className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        <span className="sr-only">{t('website.workspaceSkills.loading')}</span>
                        <WorkspaceSkillCardSkeleton />
                        <WorkspaceSkillCardSkeleton />
                        <WorkspaceSkillCardSkeleton />
                        <WorkspaceSkillCardSkeleton />
                    </div>
                ) : errorMessage ? (
                    <div className="max-w-[640px] py-5">
                        <div className="flex items-start gap-3 rounded-xl border border-[#F7C8C5] bg-[#FFF6F5] p-5">
                            <WarningCircle size={20} weight="bold" className="mt-0.5 shrink-0 text-[#B42318]" />
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-[#1A162F]">{t('website.workspaceSkills.unavailable')}</p>
                                <p className="mt-1 text-sm leading-6 text-[#666F8D]">{errorMessage}</p>
                                <button
                                    type="button"
                                    onClick={() => void loadSkills()}
                                    className="mt-3 rounded-md text-sm font-medium text-[#1A162F] transition-colors hover:text-[#3157D5] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A162F]/20"
                                >
                                    {t('website.workspaceSkills.retry')}
                                </button>
                            </div>
                        </div>
                    </div>
                ) : sortedSkills.length === 0 ? (
                    <div className="flex min-h-[260px] flex-col items-center justify-center rounded-xl border border-dashed border-[#C8D0E2] bg-[#FCFCFC] px-6 text-center">
                        <div className="flex h-11 w-11 items-center justify-center rounded-[10px] bg-[#F4F5F8] text-[#666F8D]">
                            <Lightning size={22} weight="bold" />
                        </div>
                        <p className="mt-4 text-base font-medium text-[#1A162F]">{t('website.workspaceSkills.emptyTitle')}</p>
                        <p className="mt-1 max-w-[400px] text-sm leading-6 text-[#666F8D]">
                            {t('website.workspaceSkills.emptyDescription')}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                            {sortedSkills.map((skill) => {
                                const creator = skill.createdByEmail || skill.createdByName || t('website.workspaceSkills.unknownCreator');
                                const isDeleting = deletingSkillId === skill.id;
                                return (
                                <article
                                    key={skill.id}
                                    className="group relative flex min-h-[208px] cursor-pointer flex-col rounded-xl border border-[#E8EBF3] bg-[#FCFCFC] p-5 shadow-[0_10px_24px_rgba(26,22,47,0.05)] transition-[border-color,background-color,box-shadow] duration-200 hover:border-[#C8D0E2] hover:bg-white hover:shadow-[0_12px_28px_rgba(26,22,47,0.08)] focus-within:border-[#C8D0E2] focus-within:ring-2 focus-within:ring-[#1A162F]/10"
                                >
                                    <button
                                        type="button"
                                        aria-label={`${t('website.workspaceSkills.viewSkillDetail')}: ${skill.displayName}`}
                                        disabled={isLoadingDetail || isDeleting}
                                        onClick={() => void handleViewSkillDetail(skill)}
                                        className="absolute inset-0 z-0 rounded-xl focus-visible:outline-none disabled:cursor-wait"
                                    />
                                    <div className="pointer-events-none relative z-[1] flex items-start gap-3">
                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] border border-[#E8EBF3] bg-white text-[#666F8D]">
                                            <Lightning size={19} weight="bold" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <h3 className="truncate text-[15px] font-medium leading-5 text-[#1A162F]">
                                                {skill.displayName}
                                            </h3>
                                            <p className="mt-1 overflow-hidden text-[13px] leading-5 text-[#666F8D] [display:-webkit-box] [overflow-wrap:anywhere] [-webkit-box-orient:vertical] [-webkit-line-clamp:3]">
                                                {skill.description || skill.skillKey}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            title={t('website.workspaceSkills.deleteSkill')}
                                            aria-label={t('website.workspaceSkills.deleteSkill')}
                                            disabled={isDeleting}
                                            onClick={() => void handleDelete(skill)}
                                            className={`pointer-events-auto relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[#666F8D] transition-[background-color,color,opacity] hover:bg-[#FFF1F1] hover:text-[#B42318] focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B42318]/20 disabled:cursor-not-allowed ${
                                                isDeleting
                                                    ? 'opacity-100'
                                                    : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100'
                                            }`}
                                        >
                                            {isDeleting ? (
                                                <CircleNotch size={16} weight="bold" className="animate-spin" />
                                            ) : (
                                                <Trash size={16} weight="bold" />
                                            )}
                                        </button>
                                    </div>
                                    <dl className="pointer-events-none relative z-[1] mt-auto grid min-w-0 grid-cols-[auto_minmax(0,1fr)] gap-x-2 gap-y-1 border-t border-[#EEF1F6] pt-4 text-[12px] leading-5">
                                        <dt className="shrink-0 text-[#8A93B2]">{t('website.workspaceSkills.createdBy')}</dt>
                                        <dd className="truncate font-medium text-[#1A162F]">{creator}</dd>
                                        <dt className="shrink-0 text-[#8A93B2]">{t('website.workspaceSkills.updatedAt')}</dt>
                                        <dd className="font-medium text-[#1A162F]">{formatSkillUpdatedAt(skill.updatedAt, locale)}</dd>
                                    </dl>
                                </article>
                                );
                            })}
                        </div>
                        <PaginationFooter
                            ariaLabel={t('website.workspaceSkills.pagination')}
                            className="rounded-xl border border-[#E8EBF3]"
                            currentPage={currentPage}
                            getPageLabel={(page) => t('website.workspaceSkills.pageNumber', { page })}
                            hasNextPage={currentPage < totalPages}
                            nextPageLabel={t('website.workspaceSkills.nextPage')}
                            onPageChange={(page) => {
                                closeSkillDetail();
                                setCurrentPage(page);
                            }}
                            pageNumbers={visiblePages}
                            previousPageLabel={t('website.workspaceSkills.previousPage')}
                            summary={t('website.workspaceSkills.showingSkillsRange', {
                                start: (currentPage - 1) * WORKSPACE_SKILLS_PAGE_SIZE + 1,
                                end: Math.min(currentPage * WORKSPACE_SKILLS_PAGE_SIZE, totalSkills),
                                total: totalSkills,
                            })}
                        />
                    </div>
                )}
            </div>
            {detailSkill ? (
                <DetailDrawer
                    ariaLabel={t('website.workspaceSkills.skillDetailTitle')}
                    closeLabel={t('website.workspaceSkills.closeSkillDetail')}
                    header={
                        <div className="flex min-w-0 items-center gap-3">
                            <span className="flex size-10 shrink-0 items-center justify-center rounded-[10px] border border-[#E8EBF3] bg-white text-[#666F8D]">
                                <Lightning size={19} weight="bold" />
                            </span>
                            <div className="min-w-0">
                                <h3 className="truncate text-[18px] font-medium leading-6 text-[#1A162F]">{detailSkill.displayName}</h3>
                                <div className="mt-1 flex min-w-0 items-center gap-2 text-[11px] leading-4 text-[#8A93B2]">
                                    <span className="max-w-[300px] truncate">
                                        {t('website.workspaceSkills.createdBy')} {detailCreator}
                                    </span>
                                    <span aria-hidden="true" className="shrink-0 text-[#C8D0E2]">
                                        ·
                                    </span>
                                    <span className="shrink-0">
                                        {t('website.workspaceSkills.updatedAt')} {formatSkillUpdatedAt(detailSkill.updatedAt, locale)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    }
                    onClose={closeSkillDetail}
                >
                    <div className="min-h-0 flex-1 overflow-y-auto border-t border-[#ECEFF5] px-6 py-6 [scrollbar-gutter:stable]">
                        <p className="mb-3 text-[12px] font-medium leading-5 text-[#666F8D]">SKILL.md</p>
                        {isLoadingDetail ? (
                            <SkillDetailSkeleton label={t('website.workspaceSkills.loadingSkillDetail')} />
                        ) : detailError ? (
                            <div className="rounded-xl border border-[#F7C8C5] bg-[#FFF6F5] p-4 text-sm leading-6 text-[#B42318]">
                                {detailError}
                            </div>
                        ) : (
                            <div className="rounded-xl bg-[#F8F9FC] p-5">
                                <Markdown content={getSkillDetailContent(skillMarkdown)} variant="workspaceSkill" />
                            </div>
                        )}
                    </div>
                </DetailDrawer>
            ) : null}
        </>
    );
};

export default WorkspaceSkillsSettings;
